import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Calendar, Sparkles } from 'lucide-react';

const CalendarModal = lazy(() => import('./CalendarModal'));

const Header = () => {
  const { language, changeLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 16);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { id: 'products', label: language === 'es' ? 'Productos SaaS' : 'SaaS Products' },
    { id: 'services', label: t.nav.services },
    { id: 'why-us', label: language === 'es' ? 'Nosotros' : 'About Us' },
    { id: 'process', label: t.nav.process },
    { id: 'contact', label: t.nav.contact },
  ];

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/85 backdrop-blur-xl border-b border-slate-200/80 shadow-[0_4px_20px_rgba(0,0,0,0.03)]'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a
              href="/"
              data-testid="logo-link"
              className="flex items-center gap-3 group"
            >
              <div className="relative">
                <picture>
                  <source srcSet="/assets/vegsoft-logo.webp" type="image/webp" />
                  <img
                    src="/assets/vegsoft-logo.png"
                    alt="Vegsoft Solutions"
                    width="38"
                    height="38"
                    fetchPriority="high"
                    decoding="async"
                    className="h-9 w-auto transform group-hover:scale-105 transition-transform"
                  />
                </picture>
              </div>
              <div className="flex items-baseline">
                <span className="font-heading font-extrabold text-xl text-[#0F172A] tracking-tight">Vegsoft</span>
                <span className="font-heading font-bold text-xl text-[#4F46E5] ml-1 tracking-tight">Solutions</span>
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  data-testid={`nav-${item.id}`}
                  className="text-sm font-semibold text-slate-600 hover:text-[#0F172A] transition-colors relative group py-1"
                >
                  <span>{item.label}</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#4F46E5] transition-all duration-200 group-hover:w-full rounded-full" />
                </button>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Language Selector */}
              <div className="flex items-center gap-1 bg-slate-100/80 rounded-full p-1 border border-slate-200/80">
                <button
                  onClick={() => changeLanguage('es')}
                  data-testid="lang-es"
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold transition-all ${
                    language === 'es'
                      ? 'bg-white text-[#0F172A] shadow-sm'
                      : 'text-slate-500 hover:text-[#0F172A]'
                  }`}
                >
                  <span>ES</span>
                </button>
                <button
                  onClick={() => changeLanguage('en')}
                  data-testid="lang-en"
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold transition-all ${
                    language === 'en'
                      ? 'bg-white text-[#0F172A] shadow-sm'
                      : 'text-slate-500 hover:text-[#0F172A]'
                  }`}
                >
                  <span>EN</span>
                </button>
              </div>

              {/* Stripe CTA Button */}
              <button
                onClick={() => setIsCalendarOpen(true)}
                data-testid="header-cta"
                className="hidden lg:inline-flex items-center gap-2 btn-stripe-dark text-sm"
              >
                <span>{language === 'es' ? 'Empezar ahora' : 'Get Started'}</span>
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-toggle"
                className="lg:hidden p-2 rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 transition-colors"
              >
                {isMobileMenuOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden absolute top-full left-0 w-full bg-white border-b border-slate-200 shadow-xl animate-fade-in"
          >
            <nav className="flex flex-col p-5 gap-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="text-left px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-50 hover:text-[#4F46E5] transition-colors font-semibold text-sm flex items-center justify-between"
                >
                  <span>{item.label}</span>
                  <span className="text-slate-400 text-xs">→</span>
                </button>
              ))}
              <button
                onClick={() => {
                  setIsCalendarOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                data-testid="mobile-cta"
                className="mt-3 w-full btn-stripe-dark text-sm py-3"
              >
                <Calendar className="w-4 h-4" />
                {t.nav.schedule}
              </button>
            </nav>
          </div>
        )}
      </header>

      {isCalendarOpen && (
        <Suspense fallback={null}>
          <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
        </Suspense>
      )}
    </>
  );
};

export default Header;
